import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SettingsPage } from '../../contexts/settings-modal-context';
import { SettingsTabs } from '../../components/settings-tabs';
import { Header } from '../../components/layout/header';
import { MyPlan } from '../settings/my-plan';
import { PaymentMethod } from '../settings/payment-method';
import { CallHistory } from '../settings/call-history';
import useAuth from '../../hooks/utils/useAuth';
import { ROUTE_PATH } from '../../routes/route-paths';
import { tabParamToPage, pageToTabParam } from '../../utils/settings-utils';

const MEMBERSHIP_PAGE_MAP: Record<string, SettingsPage> = {
  'my plan': SettingsPage.myPlan,
  'payment method': SettingsPage.paymentMethod,
  'call history': SettingsPage.callHistory,
};

export const MembershipSettingsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState<SettingsPage>(SettingsPage.myPlan);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const page = tabParamToPage(tabParam, MEMBERSHIP_PAGE_MAP, SettingsPage.myPlan);
    setCurrentPage(page);
  }, [location.search]);

  const handleTabChange = useCallback(
    (page: SettingsPage) => {
      setCurrentPage(page);
      history.replace(`${ROUTE_PATH.SETTINGS_MEMBERSHIP}?tab=${pageToTabParam(page)}`);
    },
    [history],
  );

  const tabs = useMemo(() => {
    const baseTabs = [{ page: SettingsPage.myPlan, label: 'My Plan' }];

    if (!user?.onUnlimitedPlan) {
      baseTabs.push({ page: SettingsPage.paymentMethod, label: 'Payment Method' });
    }

    baseTabs.push({ page: SettingsPage.callHistory, label: 'Call History' });

    return baseTabs;
  }, [user]);

  const renderPage = () => {
    switch (currentPage) {
      case SettingsPage.myPlan:
        return <MyPlan />;
      case SettingsPage.paymentMethod:
        return <PaymentMethod />;
      case SettingsPage.callHistory:
        return <CallHistory />;
      default:
        return <MyPlan />;
    }
  };

  return (
    <>
      <Header />
      <div className="bg-whiteSmoke flex min-h-screen flex-col pt-[76px]">
        {/* Tabs */}
        <SettingsTabs
          tabs={tabs}
          activeTab={currentPage}
          basePath={ROUTE_PATH.SETTINGS_MEMBERSHIP}
          onTabChange={handleTabChange}
        />
        <div className="flex-1 overflow-auto">{renderPage()}</div>
      </div>
    </>
  );
};
